import { useQuery } from '@tanstack/react-query'
import { easeedeskDashboardQuery, type EaseedeskDashboard } from '../api/easeedeskDashboard'
import { moduleOf, shellQuery } from '../api/shell'
import { DashboardCardSlot } from '../dashboard/DashboardCard'
import { usePageStyles } from '../ui/usePageStyles'

/**
 * Tabloul de bord Easeedesk - adresa `easeedesk/dashboard`, care in
 * CodeIgniter ajunge la `Dashboard::module('easeedesk')`.
 *
 * Aceleasi carduri, in aceeasi ordine, cu acelasi desen: HTML-ul unui card
 * vine gata facut din API (`CardMarkup`, adica exact `card.php`), la fel ca
 * pe tabloul central. Aceeasi foaie, `od_dashboard.css`.
 *
 * Bara de sus e cea a unui tablou de MODUL: cele doua perspective care au o
 * prima pagina scrisa (Tehnician, IT Manager), clasa de active si fereastra
 * de timp. Nu are banda de module, nici "New board"/"Arrange": prima e a
 * tabloului central, iar constructorul refuza oricum tablourile de modul -
 * butonul lui de acolo nu facea nimic.
 *
 * Fara carduri nu inseamna "gol": inseamna ori ca rolul n-are voie la
 * niciunul, ori ca n-a fost aleasa nicio firma. Se spune care din ele.
 */
export function EaseedeskDashboardPage() {
  const search = typeof window === 'undefined' ? '' : window.location.search
  const page = useQuery(easeedeskDashboardQuery(search))
  const shell = useQuery(shellQuery(moduleOf(typeof window === 'undefined' ? '' : window.location.pathname)))
  usePageStyles(['css/od_dashboard.css'], shell.data?.version ?? '')

  if (page.isPending || page.isError || !page.data) return <div className="odb-page" />

  return <EaseedeskBoardChrome page={page.data} />
}

/** `/app/easeedesk/dashboard?...` - adresa paginii, cu alegerile in ea. */
function href(view: string, days: number, klass: string): string {
  return `/app/easeedesk/dashboard?view=${view}&days=${days}&class=${klass}`
}

function EaseedeskBoardChrome({ page }: { page: EaseedeskDashboard }) {
  const days = page.scope.days
  const klass = page.scope.class
  const view = page.board.view

  return (
    <div className="odb-page" data-based-on={view}>
      <div className="odb-bar-top">
        <nav className="odb-views">
          {Object.entries(page.views).map(([key, meta]) => (
            <a className={`odb-view${key === view ? ' is-on' : ''}`} title={meta.about} href={href(key, days, klass)} key={key}>
              <i className={`fas ${meta.icon}`} aria-hidden="true"></i> {meta.label}
            </a>
          ))}
        </nav>
        <nav className="odb-classes" aria-label="Asset class">
          {Object.entries(page.classes).map(([key, meta]) => (
            <a className={`odb-class${key === klass ? ' is-on' : ''}`} title={meta.about} href={href(view, days, key)} key={key}>
              <i className={`fas ${meta.icon}`} aria-hidden="true"></i> {meta.label}
            </a>
          ))}
        </nav>
        <div className="odb-windows">
          {Object.entries(page.windows).map(([n, label]) => (
            <a
              className={`odb-window${Number(n) === days ? ' is-on' : ''}`}
              title={Number(n) === 1 ? 'The last 24 hours' : `The last ${n} days`}
              href={href(view, Number(n), klass)}
              key={n}
            >
              {label}
            </a>
          ))}
        </div>
        {(page.noCompany || !page.history) && (
          <p className="odb-lead">
            {!page.history && (
              <span className="odb-lead-warn">
                The day-by-day lines under the numbers are not being recorded yet - the dashboard tables are missing. Apply
                the schema from Change Log; the first line appears the day after, when there are two days to draw.
              </span>
            )}
            {/* Fara randul "N of M cards could not be read - that is not the same as zero" (18 septembrie
                2026): omul il citea ca pe o eroare. Un card fara date isi spune singur, linistit, "No data
                yet" - vezi `CardMarkup.php`. `tally` ramane in raspuns, neschimbat. */}
            {page.noCompany && (
              <span className="odb-lead-warn">
                No company is selected, so the cards that count within one company have nothing to read. Pick one from the
                top bar.
              </span>
            )}
          </p>
        )}
      </div>
      {page.cards.length === 0 ? (
        <div className="odb-empty">
          <i className="fas fa-lock" aria-hidden="true"></i> Nothing here is open to you. Ask an administrator for the
          rights, or pick another perspective above.
        </div>
      ) : (
        <div className="odb" id="odbGrid">
          {page.cards.map((card) => (
            <DashboardCardSlot card={card} edit={false} sizes={{}} key={card.id} />
          ))}
        </div>
      )}
    </div>
  )
}
